/**
 * Public Report Branding API
 * P1-5, P2-12: API layer for public report branding settings
 *
 * Follows the project pattern: components use API layer, not direct Firebase access.
 */
import { getDatabase, ref, get, update, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { PublicReportBranding, PUBLIC_REPORT_BRANDING_PATH } from '../types/publicReport';
import { tombstoneStorageOnlyFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';

/**
 * API client for public report branding settings
 */
class PublicReportBrandingApi {
    private database = getDatabase();
    private storage = getStorage();
    private auth = getAuth();

    /**
     * Get the current branding configuration
     */
    async getBranding(): Promise<PublicReportBranding> {
        const configRef = ref(this.database, PUBLIC_REPORT_BRANDING_PATH);
        const snapshot = await get(configRef);

        if (snapshot.exists()) {
            return snapshot.val() as PublicReportBranding;
        }
        return {};
    }

    /**
     * Upload a new logo and save branding configuration
     */
    async uploadLogo(file: File): Promise<PublicReportBranding> {
        const currentUser = this.auth.currentUser;
        if (!currentUser) {
            throw new Error('Du måste vara inloggad');
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            throw new Error('Vänligen välj en bildfil');
        }

        // Storage objects are write-once — every version uploads to a fresh
        // push-key path and the DB reference is repointed
        const logoKey = push(ref(this.database, PUBLIC_REPORT_BRANDING_PATH)).key;
        if (!logoKey) {
            throw new Error('Kunde inte skapa nyckel för logotypen');
        }

        const previousStoragePath = (await this.getBranding()).logoStoragePath;

        const storagePath = `publicAssets/publicReportBranding/${logoKey}`;
        const fileRef = storageRef(this.storage, storagePath);

        // Upload file
        await uploadBytes(fileRef, file);
        const logoUrl = await getDownloadURL(fileRef);

        // Save to database
        const updatedConfig: PublicReportBranding = {
            logoUrl,
            logoStoragePath: storagePath,
            updatedBy: currentUser.uid,
            updatedAt: formatDateForStorage(new Date()),
        };

        const configRef = ref(this.database, PUBLIC_REPORT_BRANDING_PATH);
        await update(configRef, updatedConfig);

        // Tombstone the replaced blob so the cleanup job can purge it
        if (previousStoragePath && previousStoragePath !== storagePath) {
            try {
                await tombstoneStorageOnlyFile('brandingAsset', previousStoragePath);
            } catch {
                // Best effort - tombstone failure should not block the upload
            }
        }

        recordActivity('settings.updated');
        return updatedConfig;
    }

    /**
     * Remove the current logo
     */
    async removeLogo(currentStoragePath?: string): Promise<PublicReportBranding> {
        const currentUser = this.auth.currentUser;
        if (!currentUser) {
            throw new Error('Du måste vara inloggad');
        }

        // Tombstone the old logo so a future cleanup job can purge the blob
        // (Storage is never deleted from the client)
        if (currentStoragePath) {
            try {
                await tombstoneStorageOnlyFile('brandingAsset', currentStoragePath);
            } catch {
                // Best effort - tombstone failure should not block removing the logo reference
            }
        }

        // Update database - set fields to null to remove them
        const configRef = ref(this.database, PUBLIC_REPORT_BRANDING_PATH);
        await update(configRef, {
            logoUrl: null,
            logoStoragePath: null,
            updatedBy: currentUser.uid,
            updatedAt: formatDateForStorage(new Date()),
        });
        recordActivity('settings.updated');

        return {
            updatedBy: currentUser.uid,
            updatedAt: formatDateForStorage(new Date()),
        };
    }

    /**
     * Get logo URL for use in QR code generation
     * Returns undefined if no logo configured
     */
    async getLogoUrl(): Promise<string | undefined> {
        const branding = await this.getBranding();
        return branding.logoUrl;
    }
}

/**
 * Singleton instance of the public report branding API
 */
export const publicReportBrandingApi = new PublicReportBrandingApi();
